// Centralized incentive configuration for LEGO Takeback System
// This file contains all incentive ratios and feature flag logic

// Feature flag that triggers the new incentive ratio
export const WAVE_2_FEATURE_FLAG = "lego-wave-2";

// Date after which new ratios apply (for users without feature flag)
export const INCENTIVE_CHANGE_DATE = new Date("2025-09-02T00:00:00Z");

// Incentive ratios in USD per pound
export const INCENTIVE_RATIOS = {
  LEGACY_GIFT_RATIO: 4.0, // Used for takebacks before the change date
  CURRENT_GIFT_RATIO: 2.5, // Used for takebacks on or after the change date
  LEGACY_PAYMENT_RATIO: 3.6, // Legacy payment ratio (if payment option is enabled)
  CURRENT_PAYMENT_RATIO: 2.25, // Current payment ratio (if payment option is enabled)
};

/**
 * Get the gift card incentive ratio based on feature flags and date
 * @param {Array<string>} featureFlags - Array of feature flags from the user's token
 * @param {Date|string} takebackDate - The date of the takeback (optional, defaults to now)
 * @returns {number} The incentive ratio in USD per pound
 */
export function getGiftIncentiveRatio(_, takebackDate = null) {
  const effectiveDate = takebackDate ? new Date(takebackDate) : new Date();

  if (effectiveDate >= INCENTIVE_CHANGE_DATE) {
    return INCENTIVE_RATIOS.CURRENT_GIFT_RATIO;
  }

  // Otherwise use the legacy ratio
  return INCENTIVE_RATIOS.LEGACY_GIFT_RATIO;
}

/**
 * Get the payment incentive ratio based on feature flags and date
 * @param {Array<string>} featureFlags - Array of feature flags from the user's token
 * @param {Date|string} takebackDate - The date of the takeback (optional, defaults to now)
 * @returns {number} The payment incentive ratio in USD per pound
 */
export function getPaymentIncentiveRatio(_, takebackDate = null) {
  const effectiveDate = takebackDate ? new Date(takebackDate) : new Date();

  if (effectiveDate >= INCENTIVE_CHANGE_DATE) {
    return INCENTIVE_RATIOS.CURRENT_PAYMENT_RATIO;
  }

  // Otherwise use the legacy ratio
  return INCENTIVE_RATIOS.LEGACY_PAYMENT_RATIO;
}

/**
 * Calculate the incentive value based on weight, feature flags, and date
 * @param {number} weight - Weight in pounds
 * @param {Array<string>} featureFlags - Array of feature flags from the user's token
 * @param {boolean} isGiftCard - True for gift card, false for payment
 * @param {Date|string} takebackDate - The date of the takeback (optional)
 * @returns {number} The calculated value in USD
 */
export function calculateIncentiveValue(
  weight,
  featureFlags = [],
  isGiftCard = true,
  takebackDate = null
) {
  const ratio = isGiftCard
    ? getGiftIncentiveRatio(featureFlags, takebackDate)
    : getPaymentIncentiveRatio(featureFlags, takebackDate);

  return (weight * ratio).toFixed(2);
}

/**
 * Check if using the legacy incentive ratio
 * @param {Array<string>} featureFlags - Array of feature flags from the user's token
 * @param {Date|string} takebackDate - The date of the takeback (optional)
 * @returns {boolean} True if using legacy ratio, false otherwise
 */
export function isLegacyIncentive(featureFlags = [], takebackDate = null) {
  // If has wave-2 flag, never using legacy
  if (featureFlags.includes(WAVE_2_FEATURE_FLAG)) {
    return false;
  }

  // Without flag, check date
  const effectiveDate = takebackDate ? new Date(takebackDate) : new Date();
  return effectiveDate < INCENTIVE_CHANGE_DATE;
}
